import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Printer, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApplicationDetailsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/admin-login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.role === 'parent') {
      navigate('/parent-dashboard');
      return;
    }

    fetchApplication(token);
  }, [id, navigate]);

  const fetchApplication = async (token) => {
    try {
      const response = await axios.get(`${API}/applications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplication(response.data);
    } catch (error) {
      toast.error('Failed to fetch application');
      navigate('/admin-dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadDocument = async (documentType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/documents/${id}/${documentType}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${application.admission_no}_${documentType}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Document downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!application) {
    return null;
  }

  return (
    <div className="min-h-screen" style={{ background: '#F9FAFB' }}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b print:hidden" style={{ borderColor: '#E5E7EB' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <button 
            onClick={() => navigate('/admin-dashboard')}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>
          <button 
            onClick={handlePrint}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            data-testid="print-btn"
          >
            <Printer size={20} />
            Print Application
          </button>
        </div>
      </header>

      {/* Application Details */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8 print:shadow-none print:p-0">
          {/* Print Header */}
          <div className="text-center mb-8 hidden print:block">
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>School Admission Application</h1>
            <p className="text-gray-600">Academic Year 2025-26</p>
          </div>

          {/* Application Status */}
          <div className="bg-gray-50 p-6 rounded-lg mb-8 print:border print:border-gray-300">
            <div className="grid md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">Admission Number</p>
                <p className="text-xl font-bold" style={{ color: '#002147' }}>{application.admission_no}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Application Date</p>
                <p className="font-semibold">{new Date(application.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Status</p>
                <span className={`status-badge status-${application.status.toLowerCase().replace(' ', '-')} print:border print:border-gray-400`}>
                  {application.status}
                </span>
              </div>
              {application.interview_date && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Interview</p>
                  <p className="font-semibold text-purple-700">{application.interview_date}</p>
                  <p className="text-sm text-purple-600">{application.interview_time}</p>
                </div>
              )}
            </div>
          </div>

          {/* Pupil Information */}
          <div className="mb-8 print:break-inside-avoid">
            <h2 className="text-2xl font-bold mb-4 pb-2 border-b-2" style={{ fontFamily: 'Merriweather, serif', color: '#002147', borderColor: '#D4AF37' }}>
              Pupil Information
            </h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Name:</span>
                <span className="font-semibold flex-1">{application.pupil_name}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Date of Birth:</span>
                <span className="font-semibold flex-1">{application.dob}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Place of Birth:</span>
                <span className="font-semibold flex-1">{application.place_of_birth}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Class Sought:</span>
                <span className="font-semibold flex-1">{application.class_sought}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Mother Tongue:</span>
                <span className="font-semibold flex-1">{application.mother_tongue}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Medium of Instruction:</span>
                <span className="font-semibold flex-1">{application.medium_of_instruction}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Religion/Caste:</span>
                <span className="font-semibold flex-1">{application.religion_caste}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Vaccinated:</span>
                <span className="font-semibold flex-1">{application.vaccinated ? 'Yes' : 'No'}</span>
              </div>
              {application.sats_no && (
                <div className="flex border-b pb-2">
                  <span className="text-gray-600 w-56">SATS Number:</span>
                  <span className="font-semibold flex-1">{application.sats_no}</span>
                </div>
              )}
            </div>
          </div>

          {/* Father's Information */}
          <div className="mb-8 print:break-inside-avoid">
            <h2 className="text-2xl font-bold mb-4 pb-2 border-b-2" style={{ fontFamily: 'Merriweather, serif', color: '#002147', borderColor: '#D4AF37' }}>
              Father's Information
            </h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Name:</span>
                <span className="font-semibold flex-1">{application.father_name}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Occupation:</span>
                <span className="font-semibold flex-1">{application.father_occupation}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Annual Income:</span>
                <span className="font-semibold flex-1">{application.father_income}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Qualification:</span>
                <span className="font-semibold flex-1">{application.father_qualification}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Contact Number:</span>
                <span className="font-semibold flex-1">{application.father_contact}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Email:</span>
                <span className="font-semibold flex-1">{application.father_email}</span>
              </div>
              <div className="flex border-b pb-2 md:col-span-2">
                <span className="text-gray-600 w-56">Aadhar Number:</span>
                <span className="font-semibold flex-1">{application.father_aadhar}</span>
              </div>
            </div>
          </div>

          {/* Mother's Information */}
          <div className="mb-8 print:break-inside-avoid">
            <h2 className="text-2xl font-bold mb-4 pb-2 border-b-2" style={{ fontFamily: 'Merriweather, serif', color: '#002147', borderColor: '#D4AF37' }}>
              Mother's Information
            </h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Name:</span>
                <span className="font-semibold flex-1">{application.mother_name}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Qualification:</span>
                <span className="font-semibold flex-1">{application.mother_qualification}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Contact Number:</span>
                <span className="font-semibold flex-1">{application.mother_contact}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Email:</span>
                <span className="font-semibold flex-1">{application.mother_email}</span>
              </div>
              <div className="flex border-b pb-2 md:col-span-2">
                <span className="text-gray-600 w-56">Aadhar Number:</span>
                <span className="font-semibold flex-1">{application.mother_aadhar}</span>
              </div>
            </div>
          </div>

          {/* General Information */}
          <div className="mb-8 print:break-inside-avoid">
            <h2 className="text-2xl font-bold mb-4 pb-2 border-b-2" style={{ fontFamily: 'Merriweather, serif', color: '#002147', borderColor: '#D4AF37' }}>
              General Information
            </h2>
            <div className="space-y-3 text-sm">
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Address:</span>
                <span className="font-semibold flex-1">{application.address}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Number of Dependents:</span>
                <span className="font-semibold flex-1">{application.no_of_dependents}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Pupil Staying With:</span>
                <span className="font-semibold flex-1">{application.staying_with}</span>
              </div>
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Identification Mark 1:</span>
                <span className="font-semibold flex-1">{application.identification_mark_1}</span>
              </div>
              {application.identification_mark_2 && (
                <div className="flex border-b pb-2">
                  <span className="text-gray-600 w-56">Identification Mark 2:</span>
                  <span className="font-semibold flex-1">{application.identification_mark_2}</span>
                </div>
              )}
              <div className="flex border-b pb-2">
                <span className="text-gray-600 w-56">Siblings Information:</span>
                <span className="font-semibold flex-1">{application.siblings_info}</span>
              </div>
            </div>
          </div>

          {/* Documents Submitted */}
          {application.documents && Object.keys(application.documents).length > 0 && (
            <div className="mb-8 print:break-inside-avoid">
              <h2 className="text-2xl font-bold mb-4 pb-2 border-b-2" style={{ fontFamily: 'Merriweather, serif', color: '#002147', borderColor: '#D4AF37' }}>
                Documents Submitted
              </h2>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                {Object.keys(application.documents).map(docKey => (
                  <div key={docKey} className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200 print:border print:border-gray-300">
                    <div className="flex items-center gap-3">
                      <span className="text-green-600 font-bold text-xl">✓</span>
                      <span className="font-semibold">{docKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                    </div>
                    <button
                      onClick={() => handleDownloadDocument(docKey)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition print:hidden"
                      data-testid={`download-${docKey}-btn`}
                    >
                      <Download size={16} />
                      Download
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Print-friendly document list */}
              <div className="hidden print:block mt-4">
                <p className="text-sm text-gray-600">Documents attached with this application (available for download in digital copy)</p>
              </div>
            </div>
          )}

          {/* Print Footer */}
          <div className="mt-12 pt-6 border-t text-center text-sm text-gray-600 hidden print:block">
            <p>This is a computer-generated application form. No signature is required.</p>
            <p className="mt-2">Printed on: {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetailsPage;
